import { createTheme } from '@material-ui/core/styles'
import { ThemeOptions } from '@material-ui/core/styles/createTheme'
import {
  createTheme as createThemeMuiV5,
  PaletteOptions as PaletteOptionsMuiV5,
  ThemeOptions as ThemeOptionsMuiV5,
} from '@mui/material/styles'

import '@fontsource/roboto'
import '@fontsource/inter'

import { VehicleStateEnum } from 'src/api/provider-agency'

import { Trip_LocationTypesEnum } from './api/gen'

// See https://material-ui.com/guides/typescript/ for more explanations
declare module '@material-ui/core/styles/createTheme' {
  interface ICustomPalette {
    disabled: string
    outlinedErrorHover: string
    progress: string
    success: { dark: string; light: string; main: string }
  }
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Theme {
    customPalette: ICustomPalette
    mdsPalette: {
      background: {
        filterMenu: React.CSSProperties['color']
        mainMenu: React.CSSProperties['color']
      }
    }
  }
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface ThemeOptions {
    customPalette: ICustomPalette
    mdsPalette: {
      background: {
        filterMenu: React.CSSProperties['color']
        mainMenu: React.CSSProperties['color']
      }
    }
  }
}

// MUI v5 theme declaration
declare module '@mui/material/styles' {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface PaletteOptions {
    background?: PaletteOptionsMuiV5['background']
    button?: {
      background?: string
      color?: string
    }
    divider?: string
    error?: PaletteOptionsMuiV5['error']
    home?: {
      infoCard: {
        background: string
        color: string
      }
    }
    primary?: PaletteOptionsMuiV5['primary']
    secondary?: PaletteOptionsMuiV5['secondary']
    table?: {
      evenLineBackground?: string
      oddLineBackground?: string
    }
    tooltip?: string
  }
}

export const colors = {
  black: '#192233',
  blackTransparent: 'rgba(0, 0, 0, 0.38)',
  blue: '#0171e4',
  blueLight: '#188aff',
  blueDark: '#0058b3',
  green: '#5CB85C',
  greenLight: '#7cc67c',
  greenDark: '#408040',
  greenCaribbean: '#00cfac',
  grey: '#9E9E9E',
  greyLight: '#ccc',
  greyDark: '#616161',
  greyTransparent: 'rgba(222, 226, 235, 0.6)',
  orange: '#FFA03C',
  purple: '#6c2bd4',
  pink: '#FF50F9',
  pinkDark: '#F42FB8',
  red: '#DC3030',
  redLight: '#ffd7db',
  redDark: '#9a2121',
  yellow: '#FFFC3C',
  white: '#FFFFFF',
  goldenYellow: '#FFCB37',
}

export const mdsColors = {
  subhead: '#788697',
  body: '#1F2020',
  background1: '#FFFFFF',
  background2: '#EBEDF3',
  bluePlato: '#0171E4',
  buttonBackground: '#ECF5FF',
}

export const deviceColors = {
  bicycle: colors.pinkDark,
  car: colors.blue,
  cargoBicycle: colors.green,
  deliveryBot: colors.grey,
  moped: colors.purple,
  other: colors.goldenYellow,
  scooter: colors.greenCaribbean,
}

const themeOptions: ThemeOptions = {
  typography: {
    fontFamily: 'Inter, Roboto, Helvetica, Arial, sans-serif',
    fontSize: 10,
    h2: {
      fontSize: '2rem',
      fontWeight: 'bold',
      letterSpacing: 0,
    },
    h4: {
      fontWeight: 'bold',
    },
    h5: {
      lineHeight: '19px',
      fontSize: 16,
      fontWeight: 600,
      letterSpacing: '0.15px',
    },
    h6: {
      lineHeight: '17px',
      fontSize: 14,
      fontWeight: 400,
    },
    subtitle1: {
      lineHeight: '16px',
      fontSize: 13,
      fontWeight: 400,
    },
    subtitle2: {
      lineHeight: '15px',
      fontSize: 12,
      fontWeight: 400,
    },
  },
  spacing: 5,
  palette: {
    primary: {
      light: colors.blueLight,
      main: colors.blue,
      dark: colors.blueDark,
    },
    secondary: {
      light: colors.white,
      main: colors.grey,
      dark: colors.greyDark,
    },
    error: {
      light: colors.redLight,
      main: colors.red,
      dark: colors.redDark,
    },
    background: {
      default: colors.greyTransparent,
      paper: colors.white,
    },
  },
  customPalette: {
    disabled: colors.blackTransparent,
    success: {
      light: colors.greenLight,
      main: colors.green,
      dark: colors.greenDark,
    },
    outlinedErrorHover: colors.redLight,
    progress: colors.yellow,
  },
  mdsPalette: {
    background: {
      filterMenu: colors.white,
      mainMenu: colors.black,
    },
  },
  overrides: {
    MuiSvgIcon: {
      root: {
        fontSize: '24px',
      },
    },
    MuiTooltip: {
      popper: { fontSize: '1rem' },
    },
  },
}

const themeMuiV5Options: ThemeOptionsMuiV5 = {
  typography: {
    fontFamily: 'Inter, Roboto, Helvetica, Arial, sans-serif',
    fontSize: 10,
    h2: {
      fontSize: '2rem',
      fontWeight: 'bold',
      letterSpacing: 0,
    },
    h4: {
      fontWeight: 'bold',
    },
    h5: {
      lineHeight: '19px',
      fontSize: 16,
      fontWeight: 600,
      letterSpacing: '0.15px',
    },
    h6: {
      lineHeight: '17px',
      fontSize: 14,
      fontWeight: 400,
    },
    subtitle1: {
      lineHeight: '16px',
      fontSize: 13,
      fontWeight: 400,
    },
    subtitle2: {
      lineHeight: '15px',
      fontSize: 12,
      fontWeight: 400,
    },
  },
  spacing: 5,
  palette: {
    primary: {
      light: colors.blueLight,
      main: colors.blue,
      dark: colors.blueDark,
    },
    secondary: {
      light: colors.white,
      main: colors.grey,
      dark: colors.greyDark,
    },
    error: {
      light: colors.redLight,
      main: colors.red,
      dark: colors.redDark,
    },
    background: {
      default: colors.greyTransparent,
      paper: colors.white,
    },
    button: {
      color: mdsColors.bluePlato,
      background: mdsColors.buttonBackground,
    },
    table: {
      oddLineBackground: '#F9FAFC',
      evenLineBackground: colors.white,
    },
    divider: mdsColors.background2,
    tooltip: mdsColors.subhead,
    text: {
      primary: '#333',
    },
    home: {
      infoCard: {
        background: 'rgba(253, 238, 0, 0.4)',
        color: 'rgba(245, 124, 0, 1)',
      },
    },
    success: {
      light: colors.greenLight,
      main: colors.green,
      dark: colors.greenDark,
    },
  },
}

const theme = createTheme(themeOptions)
const themeMuiV5 = createThemeMuiV5(themeMuiV5Options)
theme.shadows[1] = ['5px 5px 10px 0px rgba(0, 0, 0, 0.05)'].join(',')

export const DEVICE_STATUS_COLOR = {
  [VehicleStateEnum.available]: colors.green,
  [VehicleStateEnum.non_operational]: colors.orange,
  [VehicleStateEnum.on_trip]: colors.blue,
  [VehicleStateEnum.reserved]: colors.blue,
  [VehicleStateEnum.removed]: colors.red,
  [VehicleStateEnum.unknown]: colors.purple,
  [VehicleStateEnum.elsewhere]: colors.pink,
  [VehicleStateEnum.stopped]: colors.yellow,
}

export const LOCATION_TYPES_COLOR = {
  [Trip_LocationTypesEnum.bike_lane]: colors.green,
  [Trip_LocationTypesEnum.crosswalk]: colors.orange,
  [Trip_LocationTypesEnum.side]: colors.red,
  [Trip_LocationTypesEnum.street]: colors.blue,
  [Trip_LocationTypesEnum.garage]: colors.purple,
  [Trip_LocationTypesEnum.invalid]: colors.pink,
  [Trip_LocationTypesEnum.ends_on_side]: colors.black,
  [Trip_LocationTypesEnum.corral]: colors.grey,
  [Trip_LocationTypesEnum.curb]: colors.grey,
  [Trip_LocationTypesEnum.other_valid]: colors.grey,
  [Trip_LocationTypesEnum.rack]: colors.grey,
}

export default theme
export { themeMuiV5 }
